import type { CalendarEvent, Domain, ProjectRecord, TaskRecord } from "./api";

export const DOMAIN_LABEL: Record<Domain, string> = {
  cetec: "Cetec",
  mkbd: "Mockingbird",
  personal: "Personal",
};

export const DOMAIN_ORDER: Domain[] = ["cetec", "mkbd", "personal"];

/** Which life domain a Google account belongs to. Anything unknown is personal. */
const DOMAIN_BY_ACCOUNT: Record<string, Domain> = {
  "shaun@tech-x.com": "cetec",
  "shaun@mkbd.org": "mkbd",
};

export function domainForAccount(email: string | null): Domain {
  return (email && DOMAIN_BY_ACCOUNT[email.toLowerCase()]) || "personal";
}

export interface Filters {
  domain: Domain | "all";
  waiting: boolean;
  hasDate: boolean;
  touchedThisWeek: boolean;
}

export const DEFAULT_FILTERS: Filters = {
  domain: "all",
  waiting: false,
  hasDate: false,
  touchedThisWeek: false,
};

/** Local calendar day as YYYY-MM-DD. */
export function isoDay(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

export function addDays(day: string, n: number): string {
  const d = new Date(`${day}T00:00:00`);
  d.setDate(d.getDate() + n);
  return isoDay(d);
}

export function dayRange(start: string, count: number): string[] {
  return Array.from({ length: count }, (_, i) => addDays(start, i));
}

export function filterProjects(
  projects: ProjectRecord[],
  filters: Filters,
  today: string
): ProjectRecord[] {
  const weekAgo = addDays(today, -6);
  return projects.filter((p) => {
    if (filters.domain !== "all" && p.domain !== filters.domain) return false;
    if (filters.waiting && p.waiting === 0) return false;
    if (filters.hasDate && !(p.nextDate && p.nextDate >= today)) return false;
    if (filters.touchedThisWeek && !(p.updated && p.updated >= weekAgo)) return false;
    return true;
  });
}

/** Sort for the list: most recently updated first, undated last, then by name. */
export function sortProjects(projects: ProjectRecord[]): ProjectRecord[] {
  return [...projects].sort((a, b) => {
    const ua = a.updated ?? "";
    const ub = b.updated ?? "";
    if (ua !== ub) return ub.localeCompare(ua);
    return a.name.localeCompare(b.name);
  });
}

export function scopeProjects(projects: ProjectRecord[], selected: string | null): ProjectRecord[] {
  if (!selected) return projects;
  return projects.filter((p) => p.slug === selected);
}

export function tasksFor(tasks: TaskRecord[], projects: ProjectRecord[]): TaskRecord[] {
  const slugs = new Set(projects.map((p) => p.slug));
  return tasks.filter((t) => slugs.has(t.project));
}

export interface TaskGroup {
  project: ProjectRecord;
  tasks: TaskRecord[];
}

/** Ready (not waiting) tasks grouped by project, in the projects' given order. */
export function groupTasksByProject(tasks: TaskRecord[], projects: ProjectRecord[]): TaskGroup[] {
  const byProject = new Map<string, TaskRecord[]>();
  for (const t of tasks) {
    if (t.waiting) continue;
    const list = byProject.get(t.project) ?? [];
    list.push(t);
    byProject.set(t.project, list);
  }
  return projects
    .filter((p) => byProject.has(p.slug))
    .map((p) => ({ project: p, tasks: byProject.get(p.slug) ?? [] }));
}

export interface WaitingGroup {
  person: string;
  tasks: TaskRecord[];
}

export const UNASSIGNED = "Unassigned";

/** Waiting tasks grouped by who they wait on, largest group first. */
export function groupWaitingByPerson(tasks: TaskRecord[]): WaitingGroup[] {
  const byPerson = new Map<string, TaskRecord[]>();
  for (const t of tasks) {
    if (!t.waiting) continue;
    const key = t.waitingOn?.trim() || UNASSIGNED;
    const list = byPerson.get(key) ?? [];
    list.push(t);
    byPerson.set(key, list);
  }
  return [...byPerson.entries()]
    .map(([person, list]) => ({ person, tasks: list }))
    .sort((a, b) => {
      if (a.person === UNASSIGNED) return 1;
      if (b.person === UNASSIGNED) return -1;
      return b.tasks.length - a.tasks.length || a.person.localeCompare(b.person);
    });
}

export function filterEvents(events: CalendarEvent[], filters: Filters): CalendarEvent[] {
  if (filters.domain === "all") return events;
  return events.filter((e) => domainForAccount(e.accountEmail) === filters.domain);
}

export interface CalendarItem {
  id: string;
  day: string;
  kind: "event" | "vault";
  domain: Domain;
  /** "9:00am", "all day", "due", or "remind" */
  label: string;
  title: string;
  url?: string;
  sortKey: string;
  /** Hover detail lines: when, which calendar, where, or which project a vault date belongs to */
  detail: string[];
}

export function formatTime(date: Date): string {
  const h = date.getHours();
  const m = String(date.getMinutes()).padStart(2, "0");
  const hour12 = h % 12 || 12;
  return `${hour12}:${m}${h < 12 ? "am" : "pm"}`;
}

/** Lay Google/local events onto days. All-day events span every day up to the exclusive end. */
/** "Sep 24" from YYYY-MM-DD, for tooltip ranges. */
export function dayLabel(day: string): string {
  const d = new Date(`${day}T00:00:00`);
  return `${d.toLocaleString("en-US", { month: "short" })} ${d.getDate()}`;
}

function eventSource(e: CalendarEvent): string[] {
  const lines: string[] = [];
  const label = DOMAIN_LABEL[domainForAccount(e.accountEmail)];
  const where = e.calendarName ?? e.accountEmail;
  lines.push(where && where !== label ? `${label} · ${where}` : label);
  if (e.location) lines.push(e.location);
  return lines;
}

export function eventItems(events: CalendarEvent[], days: string[]): CalendarItem[] {
  const inRange = new Set(days);
  const items: CalendarItem[] = [];
  for (const e of events) {
    const domain = domainForAccount(e.accountEmail);
    if (e.allDay) {
      const startDay = e.start.slice(0, 10);
      const endDay = e.end.slice(0, 10); // exclusive
      const lastDay = addDays(endDay, -1);
      const when =
        lastDay > startDay
          ? `All day · ${dayLabel(startDay)}–${dayLabel(lastDay)}`
          : `All day · ${dayLabel(startDay)}`;
      const detail = [when, ...eventSource(e)];
      let cursor = startDay;
      let guard = 0;
      while (cursor < endDay && guard < 62) {
        if (inRange.has(cursor)) {
          items.push({
            id: `${e.id}:${cursor}`,
            day: cursor,
            kind: "event",
            domain,
            label: "all day",
            title: e.title,
            url: e.url ?? undefined,
            sortKey: "0",
            detail,
          });
        }
        cursor = addDays(cursor, 1);
        guard++;
      }
      continue;
    }
    const start = new Date(e.start);
    if (Number.isNaN(start.getTime())) continue;
    const day = isoDay(start);
    if (!inRange.has(day)) continue;
    const end = new Date(e.end);
    const when = Number.isNaN(end.getTime())
      ? `${dayLabel(day)} · ${formatTime(start)}`
      : `${dayLabel(day)} · ${formatTime(start)}–${formatTime(end)}`;
    items.push({
      id: e.id,
      day,
      kind: "event",
      domain,
      label: formatTime(start),
      title: e.title,
      url: e.url ?? undefined,
      sortKey: `1${String(start.getHours()).padStart(2, "0")}${String(start.getMinutes()).padStart(2, "0")}`,
      detail: [when, ...eventSource(e)],
    });
  }
  return items;
}

/** Dated vault tasks (📅) for the given projects, as calendar items. */
export function vaultDateItems(
  tasks: TaskRecord[],
  projects: ProjectRecord[],
  days: string[]
): CalendarItem[] {
  const inRange = new Set(days);
  const bySlug = new Map(projects.map((p) => [p.slug, p]));
  const items: CalendarItem[] = [];
  for (const t of tasks) {
    const project = bySlug.get(t.project);
    if (!project) continue;
    // A deadline is "due". A #remind date is only a reminder, never a deadline.
    const dates: Array<["due" | "remind", string | undefined]> = [
      ["due", t.due],
      ["remind", t.remindOn],
    ];
    for (const [label, day] of dates) {
      if (!day || !inRange.has(day)) continue;
      items.push({
        id: `vault:${t.id}:${label}`,
        day,
        kind: "vault",
        domain: project.domain,
        label,
        title: t.text,
        sortKey: label === "due" ? "2" : "3",
        detail: [
          `${label === "due" ? "Due" : "Reminder"} ${dayLabel(day)}`,
          `${DOMAIN_LABEL[project.domain]} · ${project.name}`,
          "Task in the vault",
        ],
      });
    }
  }
  return items;
}

export function itemsByDay(items: CalendarItem[], days: string[]): Map<string, CalendarItem[]> {
  const map = new Map<string, CalendarItem[]>(days.map((d) => [d, []]));
  for (const item of items) map.get(item.day)?.push(item);
  for (const list of map.values()) list.sort((a, b) => a.sortKey.localeCompare(b.sortKey));
  return map;
}

/** "Sun" / "13" for a YYYY-MM-DD day. */
export function dayParts(day: string): { dow: string; date: string; weekend: boolean } {
  const d = new Date(`${day}T00:00:00`);
  const dow = d.toLocaleDateString("en-US", { weekday: "short" });
  return { dow, date: String(d.getDate()), weekend: d.getDay() === 0 || d.getDay() === 6 };
}

/** "09-11" for list columns. */
export function shortDate(day?: string): string {
  return day ? day.slice(5) : "—";
}

/** "Mon 14" for the next-date column. */
export function shortDay(day?: string): string {
  if (!day) return "—";
  const { dow, date } = dayParts(day);
  return `${dow} ${date}`;
}

/** "2 min ago" / "3 h ago" for the freshness stamp. */
export function relativeAge(iso: string, now = Date.now()): string {
  const ms = now - new Date(iso).getTime();
  if (Number.isNaN(ms) || ms < 0) return "just now";
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  return `${Math.floor(hours / 24)} d ago`;
}
